import React, { useState } from 'react';
import ConfirmationModal from '../ConfirmationModal/ConfirmationModal';
import './styles.css';
import { IoAddCircleSharp, IoTrashBin } from 'react-icons/io5';

export interface Consequence {
    id: string;
    type: 'leve' | 'moderada' | 'grave' | 'crítica';
    description: string;
}

const CONSEQUENCE_TYPE_LABELS: Record<Consequence['type'], string> = {
    leve: '-1d durante 1 hora',
    moderada: '-1d hasta descanso',
    grave: '-2d hasta tratamiento',
    crítica: 'Incapacitado',
};

const CONSEQUENCE_TYPE_OPTIONS: { value: Consequence['type'], label: string }[] = [
    { value: 'leve', label: 'Leve' },
    { value: 'moderada', label: 'Moderada' },
    { value: 'grave', label: 'Grave' },
    { value: 'crítica', label: 'Crítica' },
];

function uuid() {
    return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

interface ConsequencesControlProps {
    value: Consequence[];
    onChange: (consequences: Consequence[]) => void;
}

const ConsequencesControl: React.FC<ConsequencesControlProps> = ({ value, onChange }) => {
    const [modalOpen, setModalOpen] = useState(false);
    const [consequenceToRemove, setConsequenceToRemove] = useState<string | null>(null);

    const handleAddConsequence = () => {
        onChange([
            ...value,
            {
                id: uuid(),
                type: 'leve',
                description: '',
            }
        ]);
    };

    const handleConsequenceChange = (id: string, field: keyof Consequence, fieldValue: string) => {
        onChange(
            value.map(c =>
                c.id === id ? { ...c, [field]: fieldValue } : c
            )
        );
    };

    const handleRemoveConsequence = (id: string) => {
        setConsequenceToRemove(id);
        setModalOpen(true);
    };

    const confirmRemoveConsequence = () => {
        if (consequenceToRemove) {
            onChange(value.filter(c => c.id !== consequenceToRemove));
            setConsequenceToRemove(null);
            setModalOpen(false);
        }
    };

    const cancelRemoveConsequence = () => {
        setConsequenceToRemove(null);
        setModalOpen(false);
    };

    return (
        <div className="consequences-section">
            <div className="consequences-header">
                <h3>Consecuencias</h3>
            </div>
            <div className="consequences-list">
                {value.map((c) => (
                    <div key={c.id} className="consequence-item">
                        <select
                            className="consequence-type"
                            value={c.type}
                            onChange={e => handleConsequenceChange(c.id, 'type', e.target.value)}
                        >
                            {CONSEQUENCE_TYPE_OPTIONS.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                        <span className="consequence-effect">
                            {CONSEQUENCE_TYPE_LABELS[c.type]}
                        </span>
                        <input
                            type="text"
                            placeholder="Descripción…"
                            value={c.description}
                            onChange={e => handleConsequenceChange(c.id, 'description', e.target.value)}
                            className="consequence-description"
                        />
                        <button
                            type="button"
                            aria-label="Eliminar consecuencia"
                            className="button-remove"
                            onClick={() => handleRemoveConsequence(c.id)}
                        >
                           <IoTrashBin />
                        </button>
                    </div>
                ))}
                <button type="button" className="button-link" onClick={handleAddConsequence}>
                    Añadir
                    <IoAddCircleSharp className='button-link-icon' />
                </button>
            </div>
            <ConfirmationModal
                open={modalOpen}
                title="¿Eliminar consecuencia?"
                onConfirm={confirmRemoveConsequence}
                onCancel={cancelRemoveConsequence}
            />
        </div>
    );
};

export default ConsequencesControl;